/**
 * Chat side-sheet drawer — канон: чат — side-sheet в шапке.
 *
 * Coached athlete reaches the coach conversation through a right-edge
 * overlay drawer, NOT a tab and NOT a route navigation. The header
 * ChatCircleDots icon (Layout.tsx) toggles this drawer. The `/chat`
 * route still works as a fallback for direct links and deep linking
 * from notifications — the route renders the same ChatPage
 * full-screen.
 *
 * Why drawer not modal:
 *  - athlete keeps the surrounding screen context visible (current
 *    workout, today metrics) while messaging — chat does not pretend
 *    to be a separate app
 *  - tap-out on backdrop closes — no commitment
 *  - mobile: panel takes full width; desktop / large viewport: panel
 *    is ~420px from right edge
 *
 * Accessibility:
 *  - role="dialog" + aria-modal="true"
 *  - aria-label keyed by i18n
 *  - Escape key closes (browser default for dialog when focused)
 *  - close X button focusable
 */
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X } from "@phosphor-icons/react";
import { ChatPage } from "../pages/ChatPage";

type Props = {
  open: boolean;
  onClose: () => void;
};

export function ChatDrawer({ open, onClose }: Props) {
  const { t } = useTranslation("nav");

  // Esc to close — bound globally only while open.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Prevent body scroll while drawer is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("chat")}
      data-testid="chat-drawer-overlay"
      className="fixed inset-0 z-50 flex justify-end bg-black/55 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        data-testid="chat-drawer-panel"
        className="bg-bg-base h-full w-full sm:max-w-[420px] shadow-drawer flex flex-col animate-slide-in-right"
        style={{ boxShadow: "var(--shadow-drawer)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drawer header — title + close. Compact: 44px tall. */}
        <div className="h-11 px-3 border-b border-bg-divider flex items-center justify-between shrink-0">
          <span className="text-sm font-semibold text-fg-primary">
            {t("chat")}
          </span>
          <button
            type="button"
            onClick={onClose}
            data-testid="chat-drawer-close"
            className="p-1.5 rounded-md text-fg-muted hover:text-fg-primary hover:bg-bg-raised transition-colors"
            aria-label={t("common:close")}
          >
            <X size={16} />
          </button>
        </div>

        {/* Embedded ChatPage uses h-full + drops the route-chrome margin. */}
        <div className="flex-1 overflow-hidden">
          <ChatPage embedded />
        </div>
      </div>
    </div>
  );
}
